import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import AdminLayout from '@/components/AdminLayout';
import { Card } from '@/components/ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  DollarSign, ShoppingBag, Users, Store, 
  TrendingUp, Scissors, Package, ArrowRight,
  TrendingDown, Wallet, ArrowUpRight, ArrowDownRight,
  PiggyBank, CreditCard, UserCheck, Calendar
} from 'lucide-react';
import HolidayModeCard from '@/components/HolidayModeCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function buildMonthOptions() {
  // Last 12 months including current
  const opts = [];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const label = d.toLocaleDateString('es-MX', { month: 'long', year: 'numeric' });
    opts.push({ value, label: label.charAt(0).toUpperCase() + label.slice(1) });
  }
  return opts;
}

function monthRange(month) {
  // month: YYYY-MM
  const [y, m] = month.split('-').map(Number);
  const start = `${y}-${String(m).padStart(2, '0')}-01`;
  const lastDay = new Date(y, m, 0).getDate();
  const end = `${y}-${String(m).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
  return { start, end };
}

export default function AdminDashboard() {
  const { getAuthHeaders, user } = useAuth();
  const monthOptions = useMemo(() => buildMonthOptions(), []);
  const [selectedMonth, setSelectedMonth] = useState(monthOptions[0].value);
  const [stats, setStats] = useState({
    todaySales: 0,
    weeklySales: 0,
    monthlySales: 0,
    transactionsToday: 0,
    branches: 0,
    clientsServed: 0,
    services: 0,
    products: 0,
    expensesTotal: 0,
    payrollTotal: 0,
    commissionsPaid: 0,
    tipsPaid: 0,
  });
  const [weeklyData, setWeeklyData] = useState([]);
  const [monthlyBalance, setMonthlyBalance] = useState(null);
  const [pendingCommissions, setPendingCommissions] = useState(null);
  const [loading, setLoading] = useState(true);
  const isCurrentMonth = selectedMonth === monthOptions[0].value;

  const fetchDashboardData = useCallback(async () => {
    setLoading(true);
    try {
      const { start, end } = monthRange(selectedMonth);
      const monthlySummaryParams = `?period=monthly&start_date=${start}&end_date=${end}`;
      const balanceParams = `?month=${selectedMonth}`;
      const clientsParams = isCurrentMonth
        ? `?period=monthly`
        : `?period=custom&start_date=${start}&end_date=${end}`;

      const [dailyReport, weeklyReport, monthlyReport, branches, services, products, balance, commissions, weeklySales, clientsServed] =
        await Promise.all([
          axios.get(`${API}/reports/sales-summary?period=daily`, getAuthHeaders()),
          axios.get(`${API}/reports/sales-summary?period=weekly`, getAuthHeaders()),
          axios.get(`${API}/reports/sales-summary${monthlySummaryParams}`, getAuthHeaders()),
          axios.get(`${API}/branches`, getAuthHeaders()),
          axios.get(`${API}/services`, getAuthHeaders()),
          axios.get(`${API}/products`, getAuthHeaders()),
          axios.get(`${API}/reports/monthly-balance${balanceParams}`, getAuthHeaders()),
          axios.get(`${API}/barbers/commissions/pending`, getAuthHeaders()),
          axios.get(`${API}/reports/weekly-sales`, getAuthHeaders()),
          axios.get(`${API}/reports/clients-served${clientsParams}`, getAuthHeaders()),
        ]);

      setStats({
        todaySales: dailyReport.data.total_sales,
        weeklySales: weeklyReport.data.total_sales,
        monthlySales: monthlyReport.data.total_sales,
        transactionsToday: dailyReport.data.transactions_count,
        branches: branches.data.length,
        clientsServed: clientsServed.data.clients_served || 0,
        services: services.data.length,
        products: products.data.length,
        expensesTotal: monthlyReport.data.expenses_total || 0,
        payrollTotal: monthlyReport.data.payroll_total || 0,
        commissionsPaid: monthlyReport.data.commissions_paid || 0,
        tipsPaid: monthlyReport.data.tips_paid || 0,
      });

      setWeeklyData(weeklySales.data.weeks || []);
      setMonthlyBalance(balance.data);
      setPendingCommissions(commissions.data);
    } catch (error) {
      toast.error('Error al cargar dashboard');
    } finally {
      setLoading(false);
    }
  }, [selectedMonth, isCurrentMonth, getAuthHeaders]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const StatCard = ({ title, value, icon: Icon, subtitle, color = 'gold', testId }) => (
    <Card data-testid={testId} className="bg-zinc-900/50 border-zinc-800 p-6 relative overflow-hidden group hover:border-zinc-700 transition-colors">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs text-zinc-500 uppercase tracking-widest mb-1">{title}</p>
          <p className={`font-heading text-3xl ${color === 'gold' ? 'text-gold' : color === 'green' ? 'text-green-500' : color === 'red' ? 'text-red-500' : 'text-white'}`}>
            {value}
          </p>
          {subtitle && <p className="text-sm text-zinc-500 mt-1">{subtitle}</p>}
        </div>
        <div className={`w-12 h-12 rounded-sm ${
          color === 'gold' ? 'bg-gold/10' : 
          color === 'green' ? 'bg-green-500/10' : 
          color === 'red' ? 'bg-red-500/10' : 
          'bg-zinc-800'
        } flex items-center justify-center`}>
          <Icon className={`w-6 h-6 ${
            color === 'gold' ? 'text-gold' : 
            color === 'green' ? 'text-green-500' : 
            color === 'red' ? 'text-red-500' : 
            'text-zinc-400'
          }`} />
        </div>
      </div>
    </Card>
  );

  const QuickLink = ({ to, title, icon: Icon }) => (
    <Link
      to={to}
      className="flex items-center justify-between p-4 bg-zinc-900/50 border border-zinc-800 rounded-sm hover:border-gold/30 transition-colors group"
    >
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-sm bg-zinc-800 group-hover:bg-gold/10 flex items-center justify-center transition-colors">
          <Icon className="w-5 h-5 text-zinc-400 group-hover:text-gold transition-colors" />
        </div>
        <span className="text-zinc-300 group-hover:text-white transition-colors">{title}</span>
      </div>
      <ArrowRight className="w-4 h-4 text-zinc-600 group-hover:text-gold transition-colors" />
    </Link>
  );

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <p className="text-zinc-500">Cargando...</p>
        </div>
      </AdminLayout>
    );
  }

  // Calculate total from weekly data
  const totalWeeklySales = weeklyData.reduce((sum, w) => sum + w.total, 0);
  const maxWeeklyValue = Math.max(...weeklyData.map(w => w.total), 1);

  return (
    <AdminLayout>
      <div className="space-y-8" data-testid="admin-dashboard">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="font-heading text-3xl text-white tracking-tight">Dashboard</h1>
            <p className="text-zinc-500 mt-1">Resumen general de tu negocio</p>
          </div>
          <div className="flex items-center gap-2" data-testid="month-selector-wrap">
            <Calendar className="w-4 h-4 text-zinc-500" />
            <Select value={selectedMonth} onValueChange={setSelectedMonth}>
              <SelectTrigger className="w-[200px] bg-zinc-900 border-zinc-800 text-white" data-testid="dashboard-month-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800">
                {monthOptions.map(opt => (
                  <SelectItem key={opt.value} value={opt.value} className="text-white" data-testid={`month-option-${opt.value}`}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Monthly Balance Card */}
        {monthlyBalance && (
          <Card className="bg-gradient-to-br from-zinc-900 to-zinc-900/50 border-zinc-800 p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <p className="text-xs text-zinc-500 uppercase tracking-widest">Balance Mensual</p>
                <p className="text-sm text-zinc-400 mt-1">
                  {monthlyBalance.month_name} • Actualizado al día
                </p>
              </div>
              <PiggyBank className="w-8 h-8 text-gold" />
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Ingresos */}
              <div className="p-4 bg-green-500/10 border border-green-500/20 rounded-sm">
                <div className="flex items-center gap-2 mb-2">
                  <ArrowUpRight className="w-4 h-4 text-green-500" />
                  <p className="text-xs text-green-500 uppercase tracking-widest">Ingresos</p>
                </div>
                <p className="font-heading text-3xl text-green-500">
                  ${monthlyBalance.income?.total?.toFixed(2) || '0.00'}
                </p>
                <div className="mt-3 space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-zinc-500">Servicios</span>
                    <span className="text-zinc-300">${monthlyBalance.income?.services?.toFixed(2) || '0.00'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-500">Productos</span>
                    <span className="text-zinc-300">${monthlyBalance.income?.products?.toFixed(2) || '0.00'}</span>
                  </div>
                  <div className="flex justify-between pt-1 border-t border-green-500/20">
                    <span className="text-zinc-500">Transacciones</span>
                    <span className="text-zinc-300">{monthlyBalance.income?.transactions_count || 0}</span>
                  </div>
                </div>
              </div>

              {/* Egresos */}
              <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-sm">
                <div className="flex items-center gap-2 mb-2">
                  <ArrowDownRight className="w-4 h-4 text-red-500" />
                  <p className="text-xs text-red-500 uppercase tracking-widest">Egresos</p>
                </div>
                <p className="font-heading text-3xl text-red-500">
                  ${monthlyBalance.expenses?.total?.toFixed(2) || '0.00'}
                </p>
                <div className="mt-3 space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-zinc-500">Comisiones Pend.</span>
                    <span className="text-zinc-300">${monthlyBalance.expenses?.commissions_pending?.toFixed(2) || '0.00'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-500">Gastos Fijos</span>
                    <span className="text-zinc-300">${monthlyBalance.expenses?.fixed_expenses?.toFixed(2) || '0.00'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-500">Costo Productos</span>
                    <span className="text-zinc-300">${monthlyBalance.expenses?.product_costs?.toFixed(2) || '0.00'}</span>
                  </div>
                </div>
              </div>

              {/* Balance */}
              <div className={`p-4 ${monthlyBalance.balance >= 0 ? 'bg-gold/10 border-gold/20' : 'bg-red-500/10 border-red-500/20'} border rounded-sm`}>
                <div className="flex items-center gap-2 mb-2">
                  <Wallet className={`w-4 h-4 ${monthlyBalance.balance >= 0 ? 'text-gold' : 'text-red-500'}`} />
                  <p className={`text-xs uppercase tracking-widest ${monthlyBalance.balance >= 0 ? 'text-gold' : 'text-red-500'}`}>
                    Balance Neto
                  </p>
                </div>
                <p className={`font-heading text-4xl ${monthlyBalance.balance >= 0 ? 'text-gold' : 'text-red-500'}`}>
                  ${monthlyBalance.balance?.toFixed(2) || '0.00'}
                </p>
                <div className="mt-3">
                  <div className="flex items-center gap-2">
                    <span className="text-zinc-500 text-sm">Margen:</span>
                    <span className={`text-sm font-semibold ${monthlyBalance.profit_margin >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                      {monthlyBalance.profit_margin?.toFixed(1) || '0'}%
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </Card>
        )}

        {/* Revenue Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard 
            title="Ventas Hoy" 
            value={`$${stats.todaySales.toFixed(2)}`} 
            icon={DollarSign}
            subtitle={`${stats.transactionsToday} transacciones`}
          />
          <StatCard 
            title="Comisiones por Pagar" 
            value={`$${pendingCommissions?.total_pending?.toFixed(2) || '0.00'}`} 
            icon={CreditCard}
            color="green"
            subtitle="Total pendiente"
          />
          <StatCard 
            title="Clientes Atendidos" 
            value={stats.clientsServed} 
            icon={UserCheck}
            color="white"
            subtitle="Este mes"
          />
          <StatCard 
            title="Sucursales" 
            value={stats.branches} 
            icon={Store}
            color="white"
          />
        </div>

        {/* Secondary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <StatCard 
            title="Gastos" 
            value={`$${(stats.expensesTotal || 0).toFixed(2)}`} 
            icon={TrendingDown}
            color="red"
            subtitle="Gastos fijos del periodo"
            testId="admin-dashboard-expenses-card"
          />
          <StatCard 
            title="Nóminas / Comisiones Pagadas" 
            value={`$${(stats.payrollTotal || 0).toFixed(2)}`} 
            icon={Wallet}
            color="gold"
            subtitle={`Comisiones $${(stats.commissionsPaid || 0).toFixed(2)} · Propinas $${(stats.tipsPaid || 0).toFixed(2)}`}
            testId="admin-dashboard-payroll-card"
          />
        </div>

        {/* Catalog Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <StatCard 
            title="Servicios" 
            value={stats.services} 
            icon={Scissors}
            color="white"
          />
          <StatCard 
            title="Productos" 
            value={stats.products} 
            icon={Package}
            color="white"
          />
        </div>

        {/* Weekly Sales Chart */}
        {weeklyData.length > 0 && (
          <Card className="bg-zinc-900/50 border-zinc-800 p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-sm text-zinc-500 uppercase tracking-widest">
                  Venta Semanal
                </h3>
                <p className="text-xs text-zinc-600 mt-1">
                  Semanas del mes actual
                </p>
              </div>
              <div className="text-right">
                <p className="text-xs text-zinc-500 uppercase tracking-widest">Total Mes</p>
                <p className="font-heading text-2xl text-gold">
                  ${totalWeeklySales.toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                </p>
              </div>
            </div>
            
            {/* Bar Chart */}
            <div className="flex items-end gap-4 h-48">
              {weeklyData.map((week, index) => {
                const height = maxWeeklyValue > 0 ? (week.total / maxWeeklyValue) * 100 : 0;
                return (
                  <div key={index} className="flex-1 flex flex-col items-center gap-3">
                    {/* Value label */}
                    <span className="text-xs text-zinc-400 font-medium">
                      ${week.total.toLocaleString('es-MX', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                    </span>
                    {/* Bar */}
                    <div className="w-full relative flex-1 flex items-end">
                      <div 
                        className="w-full bg-gradient-to-t from-gold/60 to-gold rounded-t-sm transition-all hover:from-gold/80 hover:to-gold cursor-pointer group relative"
                        style={{ height: `${Math.max(height, 8)}%` }}
                        title={`${week.label}: $${week.total.toLocaleString('es-MX')}`}
                      >
                        {/* Hover tooltip */}
                        <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-zinc-800 px-2 py-1 rounded text-xs text-white opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                          {week.transactions} ventas
                        </div>
                      </div>
                    </div>
                    {/* Week label */}
                    <div className="text-center">
                      <span className="text-sm text-white font-medium">{week.label}</span>
                      <p className="text-xs text-zinc-600">{week.start.slice(5)} - {week.end.slice(5)}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        )}

        {/* Holiday Mode - Master Admin Only */}
        {user?.role === 'master_admin' && (
          <HolidayModeCard getAuthHeaders={getAuthHeaders} />
        )}

        {/* Quick Links */}
        <div>
          <h3 className="text-sm text-zinc-500 uppercase tracking-widest mb-4">
            Accesos Rápidos
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <QuickLink to="/admin/daily-cut" title="Corte del Día" icon={DollarSign} />
            <QuickLink to="/admin/reports" title="Reportes" icon={TrendingUp} />
            <QuickLink to="/admin/barbers" title="Barberos y Comisiones" icon={Scissors} />
            <QuickLink to="/admin/branches" title="Sucursales" icon={Store} />
            <QuickLink to="/admin/products" title="Productos" icon={Package} />
            <QuickLink to="/admin/appointments" title="Citas" icon={Calendar} />
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
